import type { Id, IsoDateTime } from "../primitives";

/**
 * Shipment tracking (#527): where a parcel is, derived from its scans.
 *
 * Nothing in this file talks to a courier. It is the vocabulary the server
 * writes and the screens read: the stages, the health labels, the limits that
 * decide "No update", and the shapes of the rows the tracking routes return.
 * The derivation itself — scans in, stage out — lives in `apps/api`, because
 * only the server ever has the scans to derive from.
 */

/**
 * Where a shipment is, owned by the ERP, listed **in the order of the journey**:
 * not picked up, in transit, out for delivery, a failed attempt, delivered,
 * then the return stages, then the ones off the road. Sorting by stage uses this
 * order, in the API's SQL and in the fixture adapter alike.
 *
 * Derived from the sequence of scans, never copied from a courier's headline:
 * for DTDC a return reads plain "In Transit" in the headline while the parcel
 * is on its way back, and only the scans say so.
 */
export const SHIPMENT_STAGES = [
  "created",
  "picked_up",
  "in_transit",
  "out_for_delivery",
  "attempt_failed",
  "delivered",
  "rto_started",
  "rto_in_transit",
  "reached_origin",
  "rto_out_for_delivery",
  "rto_delivered",
  "cancelled",
  "lost",
  "damaged",
] as const;

export type ShipmentStage = (typeof SHIPMENT_STAGES)[number];

export const SHIPMENT_STAGE_LABELS: Record<ShipmentStage, string> = {
  created: "Shipment created",
  picked_up: "Picked up",
  in_transit: "In transit",
  out_for_delivery: "Out for delivery",
  attempt_failed: "Delivery attempt failed",
  delivered: "Delivered",
  rto_started: "RTO started",
  rto_in_transit: "RTO in transit",
  reached_origin: "Reached origin",
  rto_out_for_delivery: "RTO out for delivery",
  rto_delivered: "RTO delivered",
  cancelled: "Cancelled",
  lost: "Lost",
  damaged: "Damaged",
};

/**
 * Stages a shipment never leaves, so it is no longer polled. A damaged parcel
 * is not on this list: it can still be returned, and is worth following.
 */
export const FINAL_STAGES: readonly ShipmentStage[] = [
  "delivered",
  "rto_delivered",
  "cancelled",
  "lost",
];

export function isFinalStage(stage: ShipmentStage | null): boolean {
  return stage !== null && FINAL_STAGES.includes(stage);
}

/** The stages on the way back to the warehouse, before it has arrived. */
export const RETURNING_STAGES: readonly ShipmentStage[] = [
  "rto_started",
  "rto_in_transit",
  "reached_origin",
  "rto_out_for_delivery",
];

export function isReturningStage(stage: ShipmentStage | null): boolean {
  return stage !== null && RETURNING_STAGES.includes(stage);
}

/**
 * Whether a shipment needs a look.
 *
 * `no_update` takes precedence over `delayed`: a parcel that has gone quiet is
 * the thing to chase, and its being late is the symptom. Null for a shipment
 * that has reached a final stage — there is nothing left to be late for.
 */
export const SHIPMENT_HEALTHS = ["on_track", "no_update", "delayed"] as const;

export type ShipmentHealth = (typeof SHIPMENT_HEALTHS)[number];

/**
 * Health in the order a person reads a list sorted by it: the one needing the
 * most attention first, then none at all (a final shipment) last. It is the
 * same rule as the label's own precedence - a parcel that has gone quiet is the
 * one to chase, and its being late follows from that - so there is one rule,
 * not one for the label and another for the sort. Sorting by health uses this,
 * in the API's SQL and in the fixture adapter alike, so "ascending" means the
 * same thing in both. It is not the alphabet, which would put "delayed" first.
 */
export const SHIPMENT_HEALTH_SEVERITY: readonly ShipmentHealth[] = [
  "no_update",
  "delayed",
  "on_track",
];

export const SHIPMENT_HEALTH_LABELS: Record<ShipmentHealth, string> = {
  on_track: "On track",
  no_update: "No update",
  delayed: "Delayed",
};

/**
 * The five limits "No update" is judged by. Several stages share one:
 * picked up and in transit are both "in transit" to the person reading, and
 * every stage on the way back is "returning".
 */
export const NO_UPDATE_LIMIT_KEYS = [
  "created",
  "in_transit",
  "out_for_delivery",
  "attempt_failed",
  "returning",
] as const;

export type NoUpdateLimitKey = (typeof NO_UPDATE_LIMIT_KEYS)[number];

export const NO_UPDATE_LIMIT_LABELS: Record<NoUpdateLimitKey, string> = {
  created: "Shipment created, not picked up",
  in_transit: "In transit",
  out_for_delivery: "Out for delivery",
  attempt_failed: "Delivery attempt failed",
  returning: "Returning (RTO)",
};

/** Which limit judges a stage; null for a final stage, which has none. */
export function noUpdateLimitKey(
  stage: ShipmentStage | null,
): NoUpdateLimitKey | null {
  switch (stage) {
    case "created":
      return "created";
    case "picked_up":
    case "in_transit":
    case "damaged":
      return "in_transit";
    case "out_for_delivery":
      return "out_for_delivery";
    case "attempt_failed":
      return "attempt_failed";
    case "rto_started":
    case "rto_in_transit":
    case "reached_origin":
    case "rto_out_for_delivery":
      return "returning";
    case "delivered":
    case "rto_delivered":
    case "cancelled":
    case "lost":
    case null:
      return null;
  }
}

/** What Settings holds for tracking: how often to look and when to flag. */
export interface TrackingSettings {
  /** Minutes between checks of every open shipment. */
  pollIntervalMinutes: number;
  /** Hours without a scan before a shipment in that stage is flagged. */
  noUpdateHours: Record<NoUpdateLimitKey, number>;
  /** Whether Sundays count towards "no update". They do not by default. */
  skipSundays: boolean;
  /** Days without any scan before a shipment stops being polled. */
  stopAfterDays: number;
}

export const DEFAULT_TRACKING_SETTINGS: TrackingSettings = {
  pollIntervalMinutes: 180,
  noUpdateHours: {
    created: 24,
    in_transit: 24,
    out_for_delivery: 12,
    attempt_failed: 24,
    returning: 48,
  },
  skipSundays: true,
  stopAfterDays: 45,
};

/** Where a shipment's scans come from. */
export type TrackingSource = "partner" | "shopify";

/**
 * What a scan means, after translation through the one status table. `null`
 * is a status the table does not know: kept as received, never guessed.
 */
export type ScanKind =
  | "created"
  | "picked_up"
  | "in_transit"
  | "delayed"
  /** Recognised, and says nothing about where the parcel is — "Reattempt initiated". */
  | "info"
  | "out_for_delivery"
  | "attempt_failed"
  | "delivered"
  | "rto_started"
  | "rto_in_transit"
  | "reached_origin"
  | "rto_out_for_delivery"
  | "rto_delivered"
  | "lost"
  | "damaged"
  | "cancelled";

/** One scan on a shipment's timeline. */
export interface ShipmentScan {
  id: Id;
  shipmentId: Id;
  happenedAt: IsoDateTime;
  /** As the source wrote it; empty for a DTDC scan that carries only a code. */
  status: string;
  code: string | null;
  location: string | null;
  remark: string | null;
  source: TrackingSource;
  /** `null` when the status table does not know this scan. */
  kind: ScanKind | null;
  /** False for a scan the table does not know — listed as "unrecognised". */
  recognised: boolean;
}

/** What a logistics partner can do. Only tracking is built so far. */
export const COURIER_CAPABILITIES = [
  "tracking",
  "rates",
  "create_shipment",
  "failed_delivery_actions",
  "remittance",
] as const;

export type CourierCapability = (typeof COURIER_CAPABILITIES)[number];

/** One row of Settings > Logistics partners. */
export interface LogisticsPartner {
  /** The registry key — `ithink`, `shipmaxx`, or `shopify` for the fallback. */
  key: string;
  name: string;
  /** The Shopify scan-history fallback is listed too, so coverage is visible. */
  kind: "partner" | "fallback";
  /** Keys configured. The fallback is connected when Shopify credentials are. */
  connected: boolean;
  capabilities: Record<CourierCapability, boolean>;
  /** Open shipments this source follows. */
  openShipments: number;
  /** The last check that finished without failing. */
  lastSuccessAt: IsoDateTime | null;
  lastError: string | null;
  lastErrorAt: IsoDateTime | null;
}

/**
 * `skipped`: the run was asked for and checked nothing, and `note` says why -
 * no open shipment, or no source that can read them. It is a row so that
 * pressing "Check now" is never answered with silence.
 */
export type TrackingRunStatus =
  "running" | "ok" | "partial" | "failed" | "skipped";

/** One run of the poller against one source. A failure is a row, not a log line. */
export interface TrackingRun {
  id: Id;
  /** The partner key, or `shopify` for the fallback. */
  source: string;
  trigger: "schedule" | "manual" | "order";
  status: TrackingRunStatus;
  startedAt: IsoDateTime;
  finishedAt: IsoDateTime | null;
  /** Shipments looked at. */
  checked: number;
  /** Shipments whose stage, scans or dates changed. */
  updated: number;
  /** Shipments that could not be checked. */
  failures: number;
  /** What went wrong, for a `failed` or `partial` run. Never a key or a response body. */
  error: string | null;
  /** Why a `skipped` run checked nothing, in words a person can read; null otherwise. */
  note: string | null;
}

/** A status no table entry covers, with how often it was seen. */
export interface UnrecognisedStatus {
  source: TrackingSource;
  status: string;
  code: string | null;
  count: number;
  lastSeenAt: IsoDateTime;
}

/*
  What the screens add to the vocabulary above (#527, Part B): plain words for a
  scan, the names of the partners, the bounds a setting accepts, and the shapes
  the tracking repository answers with. Still nothing that talks to a courier.
*/

/**
 * What a scan is called on a timeline, in words a person reads. A scan that
 * moved the parcel reads as the stage it moved it to. `info` says nothing
 * about where the parcel is ("Reattempt initiated"), so a timeline shows the
 * courier's own words for it and this is only the fallback.
 */
export const SCAN_KIND_LABELS: Record<ScanKind, string> = {
  created: SHIPMENT_STAGE_LABELS.created,
  picked_up: SHIPMENT_STAGE_LABELS.picked_up,
  in_transit: SHIPMENT_STAGE_LABELS.in_transit,
  delayed: "Delayed",
  info: "Courier update",
  out_for_delivery: SHIPMENT_STAGE_LABELS.out_for_delivery,
  attempt_failed: SHIPMENT_STAGE_LABELS.attempt_failed,
  delivered: SHIPMENT_STAGE_LABELS.delivered,
  rto_started: SHIPMENT_STAGE_LABELS.rto_started,
  rto_in_transit: SHIPMENT_STAGE_LABELS.rto_in_transit,
  reached_origin: SHIPMENT_STAGE_LABELS.reached_origin,
  rto_out_for_delivery: SHIPMENT_STAGE_LABELS.rto_out_for_delivery,
  rto_delivered: SHIPMENT_STAGE_LABELS.rto_delivered,
  lost: SHIPMENT_STAGE_LABELS.lost,
  damaged: SHIPMENT_STAGE_LABELS.damaged,
  cancelled: SHIPMENT_STAGE_LABELS.cancelled,
};

/** What a screen calls the fallback source. */
export const SHOPIFY_SOURCE_NAME = "Shopify";

/**
 * The partners the system knows, by registry key, for a screen that has a key
 * (`Shipment.trackingPartner`) and no right to read the partners route.
 *
 * The server's registry is where a partner is really defined, and its name
 * there is the one Settings shows. This copy exists because an order's page is
 * read by people who hold `orders.detail.view` and not `settings.sync.view`. A
 * test in `apps/api` fails when a registered partner is missing from it or
 * named differently, so adding a partner cannot quietly leave this behind.
 */
export const TRACKING_PARTNER_NAMES: Readonly<Record<string, string>> = {
  ithink: "iThink Logistics",
  shipmaxx: "ShipMaxX",
};

/** A partner's name from its registry key; the key itself for one this build has not heard of. */
export function trackingPartnerName(key: string): string {
  return TRACKING_PARTNER_NAMES[key] ?? key;
}

/**
 * Where a shipment's tracking came from, in words: the partner's name, or
 * Shopify. Null before the first check.
 */
export function trackingSourceName(shipment: {
  trackingSource: TrackingSource | null;
  trackingPartner: string | null;
}): string | null {
  if (shipment.trackingSource === null) return null;
  if (shipment.trackingSource === "shopify") return SHOPIFY_SOURCE_NAME;
  return shipment.trackingPartner === null
    ? "Courier partner"
    : trackingPartnerName(shipment.trackingPartner);
}

/**
 * The bounds a tracking setting accepts. The API refuses a value outside them
 * with a 422 naming the field; the settings form states them beside the field
 * and the fixture adapter refuses by the same numbers.
 */
export const TRACKING_SETTING_BOUNDS = {
  pollIntervalMinutes: { min: 15, max: 24 * 60 },
  noUpdateHours: { min: 1, max: 30 * 24 },
  stopAfterDays: { min: 7, max: 365 },
} as const;

/** What `GET /api/tracking/settings` answers: what is in force, and what it may be set to. */
export interface TrackingSettingsState {
  /** In force now. */
  settings: TrackingSettings;
  /** What "Reset to defaults" sets. */
  defaults: TrackingSettings;
  scheduler: {
    /** The timer is on at all (`TRACKING_INTERVAL_MINUTES` is not 0). */
    enabled: boolean;
    /** A check is going now. */
    running: boolean;
    /** When the next scheduled check starts; null when the timer is off. */
    nextRunAt: IsoDateTime | null;
  };
  bounds: {
    pollIntervalMinutes: { min: number; max: number };
    noUpdateHours: { min: number; max: number };
    stopAfterDays: { min: number; max: number };
  };
}

/** A change to the settings: only the keys sent are changed. */
export interface TrackingSettingsPatch {
  pollIntervalMinutes?: number;
  noUpdateHours?: Partial<Record<NoUpdateLimitKey, number>>;
  skipSundays?: boolean;
  stopAfterDays?: number;
}

/** What a saved change answers with: what is in force now. */
export interface TrackingSettingsSaved {
  settings: TrackingSettings;
  /** False when what was sent is what was already in force: nothing was saved. */
  changed: boolean;
}

/**
 * What pressing "Check now" came to. Every outcome is something to say in
 * words, so none is an error: a check that found nothing to do, or was asked
 * for twice, is an ordinary answer.
 */
export type TrackingCheckOutcome =
  /** Started; the outcome lands in the recent checks. */
  | { kind: "started"; sources: string[] }
  /** A check was already going, and this joined it. */
  | { kind: "already_running" }
  /** Nothing to check, with the reason in words. A row was written for it. */
  | { kind: "nothing_to_check"; reason: string }
  /** The same scope was just checked; try again after this many seconds. */
  | { kind: "just_checked"; retryAfterSeconds: number }
  /** The named source is not connected, or is not one. */
  | { kind: "not_connected"; message: string }
  /** Tracking is not set up on this server. */
  | { kind: "unavailable"; message: string }
  /** The caller may not run a check; the permission it wants, when the server said. */
  | { kind: "forbidden"; permission: string | null };

/** What one source did when an order was refreshed. */
export interface TrackingSourceReport {
  /** A partner key, or `shopify`. */
  source: string;
  status: TrackingRunStatus;
  checked: number;
  updated: number;
  failures: number;
  error: string | null;
}

/** What "Refresh tracking" on an order came to. */
export type TrackingRefreshOutcome =
  /** Asked; each source's answer is in `sources`. */
  | { kind: "refreshed"; sources: TrackingSourceReport[] }
  /** Refreshed a moment ago; try again after this many seconds. */
  | { kind: "just_refreshed"; retryAfterSeconds: number }
  /** The order has no open shipment: no number yet, or every parcel has finished. */
  | { kind: "nothing_to_check" }
  /** An open shipment exists and no source that is connected can answer for it. */
  | { kind: "not_connected" }
  | { kind: "not_found" }
  | { kind: "unavailable"; message: string }
  | { kind: "forbidden"; permission: string | null };
